import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import type { AchievementView } from "@frontier/content/achievements";
import type { Profile } from "@frontier/protocol/contracts";
import type { ProfileSync } from "./profile-sync";
import { api } from "./session-api";

export interface AchievementControls {
  view: AchievementView | null;
  busy: boolean;
  error: string;
  reload: () => Promise<void>;
  cancel: () => void;
}
export function useAchievements(profile: Profile | null, token: RefObject<string>, sync: ProfileSync): AchievementControls {
  const active = useRef(profile?.playerId);
  active.current = profile?.playerId;
  const request = useRef<{ controller: AbortController; playerId: string; epoch: number; token: string } | null>(null);
  const sequence = useRef(0);
  const epoch = sync.ticket(), session = token.current;
  const [state, setState] = useState<{ playerId: string; epoch: number; token: string; view: AchievementView | null; busy: boolean; error: string } | null>(null);
  useEffect(() => () => { request.current?.controller.abort(); sequence.current++; }, []);
  const cancel = useCallback(() => {
    if (!request.current) return;
    request.current.controller.abort();
    request.current = null;
    sequence.current++;
    setState(previous => previous ? { ...previous, busy: false } : previous);
  }, []);
  const reload = useCallback(async () => {
    const playerId = active.current, session = token.current, epoch = sync.ticket();
    if (!playerId || !session) return;
    const previous = request.current;
    if (previous?.playerId === playerId && previous.epoch === epoch && previous.token === session) return;
    previous?.controller.abort();
    const controller = new AbortController();
    request.current = { controller, playerId, epoch, token: session };
    const id = ++sequence.current;
    const owns = () => !controller.signal.aborted && id === sequence.current && active.current === playerId && token.current === session && sync.ticket() === epoch;
    setState(previous => ({ playerId, epoch, token: session,
      view: previous?.playerId === playerId && previous.epoch === epoch && previous.token === session ? previous.view : null, busy: true, error: "" }));
    try {
      const view = await api.achievements(session, controller.signal);
      if (owns()) {
        setState({ playerId, epoch, token: session, view, busy: false, error: "" });
        await sync.refresh(epoch);
      }
    } catch (error) {
      if (owns()) setState(previous => ({ playerId, epoch, token: session, view: previous?.view ?? null, busy: false,
        error: error instanceof Error ? error.message : "Не удалось загрузить достижения." }));
    } finally {
      if (request.current?.controller === controller) request.current = null;
    }
  }, [profile?.playerId, session, epoch, token, sync]);
  const visible = state?.playerId === profile?.playerId && state?.epoch === epoch && state?.token === session;
  return { view: visible ? state.view : null, busy: visible && state.busy, error: visible ? state.error : "", reload, cancel };
}
